import React, { useState, useEffect } from "react";
import "../css/Navbarlanding.css";
import { useNavigate, useLocation } from "react-router-dom";
import useAuthStore from "../store/useAuthStore";
import { getBuilderHomePath } from "../utils/builderNavigation";
import { delayedToast, isButtonDisabled } from "../lib/customToast.js";
import GenericModal from "./common/GenericModal";
import { User } from "lucide-react"; // Import the User icon

const Navbarlanding = ({ transparent, transparentLightText = false }) => {
  const [activeLink, setActiveLink] = useState("HOME");
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { token, userType, user } = useAuthStore(); // Get user object

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Removed dropdown logic since we're using modal now
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleLoginRedirect = (selectedType) => {
    // Determine the user type based on current path if it's a specific page
    const effectiveUserType = location.pathname === "/constructionconsultation" ? "consultant" : selectedType;

    if (token && userType) {
      if (userType !== effectiveUserType) {
        delayedToast.error(`You are already logged in as a ${userType}.`, {}, "navbar-login-redirect-btn");
        return;
      }

      if (userType === "builder") {
        navigate(getBuilderHomePath(user));
      } else if (userType === "consultant") {
        navigate("/consultant/dashboard");
      } else {
        navigate("/customer/projectinvite");
      }
    } else {
      if (effectiveUserType === "consultant") {
        navigate("/consultant/login");
      } else {
        navigate(`/login?type=${effectiveUserType}`);
      }
    }

    setIsSignupModalOpen(false);
  };

  const handleSignupCardClick = (signupUserType) => {
    setIsSignupModalOpen(false);
    if (signupUserType === "builder") {
      navigate("/builder/signup");
    } else if (signupUserType === "consultant") {
      navigate("/consultant/signup");
    } else {
      navigate("/customer/signup");
    }
  };

  const handleActiveLink = (navlink, event) => {
    event.preventDefault();
    setActiveLink(navlink);
    if (navlink === "HOME") {
      navigate("/");
    }
    if (navlink === "SUPPORT") {
      navigate("/support");
    }
    if (navlink === "CAREER") {
      navigate("/job-portal");
      window.scrollTo(0, 0); // Ensure scroll to top
    }
  };

  const scrollToServices = () => {
    const servicesSection = document.querySelector("#services");
    if (servicesSection) {
      const yOffset =
        servicesSection.getBoundingClientRect().top + window.pageYOffset - 100;
      window.scrollTo({ top: yOffset, behavior: "smooth" });
    }
    setActiveLink("SERVICES");
  };

  const navLinkClass = (linkName) => {
    const isActive = activeLink === linkName;
    const base =
      "relative px-4 py-2 text-[14px] font-bold font-poppins transition-all hover:rounded-full duration-300";
    if (transparent && transparentLightText) {
      return `${base} hover:text-gray-300 ${isActive ? "text-gray-300" : "text-white"}`;
    }
    return `${base} hover:text-amber-600 hover:bg-amber-50 ${
      isActive ? "text-amber-600" : "text-black"
    }`;
  };

  const joinUsClass = () => {
    const base =
      "relative px-4 py-2 text-[14px] font-bold font-poppins transition-all hover:rounded-full duration-300";
    if (transparent && transparentLightText) {
      return `${base} text-white hover:text-gray-300`;
    }
    return `${base} text-black hover:text-amber-600 hover:bg-amber-50`;
  };

  return (
    <nav
      className={`transition-all duration-300 ${
        transparent ? "bg-transparent shadow-none" : "bg-white shadow-md"
      }`}
    >
      <div className="max-w-[90rem] mx-auto px-2  sm:px-4 lg:px-6">
        <div className="flex justify-between items-center h-16">
          <div
            className="flex-shrink-0 cursor-pointer"
            onClick={() => navigate("/")}
          >
            <img
              src={
                transparent && transparentLightText
                  ? "/assets/Transparent background.png"
                  : "/assets/black logo.png"
              }
              alt="Ducktail"
              className="h-8 sm:h-14"
            />
          </div>
          <div className="hidden lg:flex space-x-3 items-center">
            <a href=" " onClick={(e) => handleActiveLink("HOME", e)} className={navLinkClass("HOME")}>
              HOME
            </a>
            <a
              href="#services"
              onClick={(e) => {
                e.preventDefault();
                if (location.pathname !== "/") {
                  navigate("/", { state: { scrollToServices: true } });
                } else {
                  scrollToServices();
                }
                setActiveLink("SERVICES");
              }}
              className={navLinkClass("SERVICES")}
            >
              SERVICES
            </a>
            <button type="button" onClick={() => setIsSignupModalOpen(true)} className={joinUsClass()}>
              JOIN US
            </button>
            <a href=" " onClick={(e) => handleActiveLink("SUPPORT", e)} className={navLinkClass("SUPPORT")}>
              SUPPORT
            </a>

            {/* --- MODIFICATION START --- */}
            <div className="hidden lg:block" style={{ marginLeft: "60px" }}>
              {token && user ? (
                <button
                  onClick={() => {
                    const dashboardPath = userType === 'builder' ? getBuilderHomePath(user) : userType === 'consultant' ? '/consultant/dashboard' : '/customer/profile';
                    navigate(dashboardPath);
                  }}
                  className="flex items-center gap-2 bg-gray-900 text-white font-bold font-poppins text-xs px-4 py-2 rounded-full hover:bg-black transition-all shadow-lg"
                >
                  <User className="w-4 h-4" />
                  {userType === 'consultant' ? `Hello Engineer ${user.name?.split(' ')[0] || 'Consultant'}` : `Hello, ${user.name?.split(' ')[0] || 'User'}`}
                </button>
              ) : (
                <button
                  onClick={() => handleLoginRedirect("customer")}
                  disabled={isButtonDisabled("navbar-login-redirect-btn")}
                  className="bg-gray-900 text-white font-bold font-poppins px-6 py-2 rounded-full hover:bg-black transition-all shadow-lg disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {location.pathname === "/constructionconsultation" ? "Consultant Login" : "Customer Login"}
                </button>
              )}
            </div>
            {/* --- MODIFICATION END --- */}

          </div>
          {/* Removed hamburger menu button and mobile menu */}
        </div>
      </div>

      {/* Removed mobile menu dropdown */}

      {/* Signup Modal */}
      <GenericModal
        isOpen={isSignupModalOpen}
        onClose={() => setIsSignupModalOpen(false)}
        title="Join Ducktail As"
        size="max-w-lg"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4">
          <div
            onClick={() => handleSignupCardClick("builder")}
            className="flex flex-col items-center p-4 border border-gray-300 rounded-lg hover:shadow-lg hover:border-amber-500 transition-all duration-200 cursor-pointer bg-gray-50 text-center"
          >
            <img
              src="/assets/builder.png"
              alt="Builder"
              className="w-16 h-16 object-contain mb-3"
            />
            <h3 className="text-sm font-semibold text-gray-700">Builder</h3>
            <p className="text-[10px] text-gray-500 mt-1">
              Showcase projects and connect with clients.
            </p>
          </div>
          <div
            onClick={() => handleSignupCardClick("customer")}
            className="flex flex-col items-center p-4 border border-gray-300 rounded-lg hover:shadow-lg hover:border-amber-500 transition-all duration-200 cursor-pointer bg-gray-50 text-center"
          >
            <img
              src="/assets/young-man.png"
              alt="Customer"
              className="w-16 h-16 object-contain mb-3"
            />
            <h3 className="text-sm font-semibold text-gray-700">Customer</h3>
            <p className="text-[10px] text-gray-500 mt-1">
              Find verified builders for your dream project.
            </p>
          </div>
        </div>
      </GenericModal>
    </nav>
  );
};

export default Navbarlanding;